// SEO Utilities for ClubHosty
// Enhanced meta tags and structured data for better indexing

export const generateStructuredData = (type, data) => {
  const baseStructuredData = {
    "@context": "https://schema.org",
    "@type": type,
    ...data
  };

  return JSON.stringify(baseStructuredData);
};

// Organization Schema for ClubHosty
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "ClubHosty",
  "alternateName": "Club Hosty",
  "description": "Best Affordable Web Hosting Provider in India offering shared hosting, VPS hosting, cloud hosting, and domain registration services.",
  "url": "https://www.clubhosty.com",
  "logo": "https://www.clubhosty.com/Images/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-011-6931-0286",
    "contactType": "customer service",
    "availableLanguage": ["English", "Hindi"]
  },
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IN",
    "addressRegion": "India"
  },
  "sameAs": [
    "https://www.facebook.com/clubhosty",
    "https://twitter.com/clubhosty",
    "https://www.linkedin.com/company/clubhosty"
  ],
  "foundingDate": "2020",
  "numberOfEmployees": "10-50",
  "slogan": "Affordable Web Hosting Solutions for Everyone"
};

// Product Schema Generator for hosting services
export const generateProductSchema = (productName, description, price, currency = "INR") => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": productName,
  "description": description,
  "brand": {
    "@type": "Brand",
    "name": "ClubHosty"
  },
  "offers": {
    "@type": "Offer",
    "price": price,
    "priceCurrency": currency,
    "availability": "https://schema.org/InStock",
    "seller": {
      "@type": "Organization",
      "name": "ClubHosty"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "150"
  }
});

// FAQ Schema Generator
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Website Schema
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "ClubHosty",
  "alternateName": "Club Hosty",
  "url": "https://www.clubhosty.com",
  "description": "Best Affordable Web Hosting Provider in India",
  "publisher": {
    "@type": "Organization",
    "name": "ClubHosty"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://www.clubhosty.com/domain?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

// Service Schema Generator
export const generateServiceSchema = (serviceName, description, serviceType = "WebHostingService") => ({
  "@context": "https://schema.org",
  "@type": serviceType,
  "name": serviceName,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": "ClubHosty",
    "url": "https://www.clubhosty.com"
  },
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Web Hosting Services",
    "itemListElement": [{
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": serviceName
      }
    }]
  }
});

// Enhanced Meta Tags Generator
export const generateMetaTags = (pageConfig) => {
  const {
    title,
    description,
    keywords,
    canonicalUrl,
    ogImage = "https://www.clubhosty.com/Images/logo.png",
    ogType = "website",
    twitterCard = "summary_large_image"
  } = pageConfig;

  return {
    title,
    metaTags: [
      // Basic meta tags
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      { name: "robots", content: "index, follow" },
      { name: "author", content: "ClubHosty" },
      
      // Open Graph meta tags
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: ogType },
      { property: "og:url", content: canonicalUrl },
      { property: "og:image", content: ogImage },
      { property: "og:site_name", content: "ClubHosty" },
      
      // Twitter Card meta tags
      { name: "twitter:card", content: twitterCard },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
      { name: "twitter:site", content: "@clubhosty" },
      
      // Additional SEO meta tags
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "theme-color", content: "#21a2a0" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" }
    ],
    canonicalUrl
  };
};

// Page-specific configurations
export const pageConfigs = {
  home: {
    title: "Best Affordable Web Hosting Provider in India | ClubHosty",
    description: "Get Best Affordable Web Hosting Provider in India from ClubHosty. Affordable WordPress hosting plans, ensuring fast, reliable, and seamless online experiences. Buy now!",
    keywords: "best affordable web hosting provider in india, cheap web hosting, wordpress hosting, shared hosting india, web hosting services",
    canonicalUrl: "https://www.clubhosty.com/"
  },
  cpanelHosting: {
    title: "Best Affordable cPanel Web Hosting Services in India | ClubHosty",
    description: "Get best affordable cPanel hosting services in India with ClubHosty cheap cPanel Web hosting plans for best website management and reliable performance",
    keywords: "affordable cpanel web hosting services, cpanel hosting india, cheap cpanel hosting, web hosting with cpanel",
    canonicalUrl: "https://www.clubhosty.com/affordable-cpanel-hosting"
  },
  sharedHosting: {
    title: "Best Cheap Shared Web Hosting Plans in India | ClubHosty",
    description: "Get best cheap shared web hosting plans in India with ClubHosty. Reliable shared hosting services with 99.9% uptime, free SSL, and 24/7 support.",
    keywords: "cheap shared web hosting, shared hosting india, affordable shared hosting plans, best shared hosting",
    canonicalUrl: "https://www.clubhosty.com/sharedHosting"
  },
  vpsHosting: {
    title: "Affordable Linux VPS Hosting in India | ClubHosty",
    description: "Get affordable Linux VPS hosting in India with ClubHosty. Powerful VPS servers with SSD storage, root access, and guaranteed resources at best prices.",
    keywords: "affordable linux vps hosting, vps hosting india, cheap vps hosting, linux vps server",
    canonicalUrl: "https://www.clubhosty.com/affordable-linux-vps-hosting"
  },
  cloudHosting: {
    title: "Cloud Based Hosting Services in India | ClubHosty",
    description: "Get reliable cloud based hosting services in India with ClubHosty. Scalable cloud hosting solutions with high performance and guaranteed uptime.",
    keywords: "cloud based hosting services, cloud hosting india, scalable cloud hosting, cloud web hosting",
    canonicalUrl: "https://www.clubhosty.com/cloud-based-hosting-services"
  },
  resellerHosting: {
    title: "Affordable Reseller Web Hosting in India | ClubHosty",
    description: "Start your hosting business with ClubHosty's affordable reseller web hosting in India. White-label hosting solutions with WHM/cPanel and 24/7 support.",
    keywords: "affordable reseller web hosting, reseller hosting india, whm cpanel reseller, hosting reseller program",
    canonicalUrl: "https://www.clubhosty.com/affordable-reseller-web-hosting"
  },
  domain: {
    title: "Search For Web Domains | Register Domain Names | ClubHosty",
    description: "Search for and register web domains at ClubHosty. Find the perfect domain name for your business and secure it at best prices with free WHOIS privacy.",
    keywords: "domain registration, buy domain names, cheap domain registration, domain search",
    canonicalUrl: "https://www.clubhosty.com/domain"
  },
  contact: {
    title: "Contact Us - ClubHosty | Web Hosting Support",
    description: "Contact ClubHosty for web hosting support, sales inquiries, and technical assistance. Get 24/7 support for all your hosting needs in India.",
    keywords: "contact clubhosty, web hosting support, hosting help, customer support",
    canonicalUrl: "https://www.clubhosty.com/contact"
  },
  tools: {
    title: "Free Web Tools | IP Address, DNS Records, WHOIS Lookup",
    description: "Free web tools including IP address lookup, DNS records checker, WHOIS domain lookup, and nameserver checker. Essential tools for webmasters.",
    keywords: "free web tools, ip address lookup, dns records, whois lookup, nameserver checker",
    canonicalUrl: "https://www.clubhosty.com/"
  },
  ipAddress: {
    title: "IP Address Lookup Tool | Find Your IP Address | ClubHosty",
    description: "Free IP address lookup tool to find your public IP address, location, ISP information, and network details instantly.",
    keywords: "ip address lookup, find my ip, ip address checker, what is my ip",
    canonicalUrl: "https://www.clubhosty.com/ip-address"
  },
  dnsRecords: {
    title: "DNS Records Checker | DNS Lookup Tool | ClubHosty",
    description: "Free DNS records checker tool to lookup and verify DNS records including A, AAAA, MX, CNAME, TXT, and NS records for any domain.",
    keywords: "dns records checker, dns lookup, dns records tool, check dns records",
    canonicalUrl: "https://www.clubhosty.com/dns-records"
  },
  whois: {
    title: "WHOIS Lookup Tool | Domain Information Checker | ClubHosty",
    description: "Free WHOIS lookup tool to check domain information, registration details, expiry dates, and nameservers for any domain name.",
    keywords: "whois lookup, domain whois, domain information, whois checker",
    canonicalUrl: "https://www.clubhosty.com/whois"
  },
  nameserver: {
    title: "Nameserver Checker Tool | DNS Nameserver Lookup | ClubHosty",
    description: "Free nameserver checker tool to verify and lookup nameserver information for any domain name instantly.",
    keywords: "nameserver checker, dns nameserver, nameserver lookup, check nameservers",
    canonicalUrl: "https://www.clubhosty.com/nameserver"
  }
};
